import AppLogo from 'components/header/AppLogo'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

const footerLinkClass =
  'inline-block text-left text-sm text-slate-500 transition-colors hover:text-imovPrimary focus:outline-none focus:ring-0'

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 8.25v8.25a4.5 4.5 0 11-4.5-4.5h1.5m3-6v3a9 9 0 009 9"
      />
    </svg>
  )
}

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="4" ry="4" />
      <circle cx="12" cy="12" r="3.5" />
      <path strokeLinecap="round" d="M16.5 7.5h.01" />
    </svg>
  )
}

function ThreadsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path strokeLinecap="round" d="M10.5 9.75c1.5-1.5 4.2-1.2 4.2 1.35 0 1.8-2.1 2.55-3.45 1.65M13.5 14.25c-1.5 1.5-4.2 1.2-4.2-1.35 0-1.8 2.1-2.55 3.45-1.65" />
    </svg>
  )
}

const Footer = () => {
  const { t } = useTranslation()

  const servicesLinks: { key: string; to: string }[] = [
    { key: 'linkTransfer', to: '/create-ad' },
    { key: 'linkFindHousing', to: '/listings' },
    { key: 'linkCreditCheck', to: '/contact' },
    { key: 'linkLeaseTemplates', to: '/contact' },
  ]

  const companyLinks: { key: string; to: string }[] = [
    { key: 'linkAbout', to: '/contact' },
    { key: 'linkBlog', to: '/contact' },
    { key: 'linkCareers', to: '/contact' },
    { key: 'linkContact', to: '/contact' },
  ]

  const legalLinks: { key: string; to: string }[] = [
    { key: 'linkTerms', to: '/terms-of-use' },
    { key: 'linkPrivacy', to: '/terms-of-use' },
    { key: 'linkCookies', to: '/terms-of-use' },
  ]

  const social = [
    { Icon: TikTokIcon, labelKey: 'home.footer.socialTiktok', href: 'https://www.tiktok.com' },
    { Icon: InstagramIcon, labelKey: 'home.footer.socialInstagram', href: 'https://www.instagram.com' },
    { Icon: ThreadsIcon, labelKey: 'home.footer.socialThreads', href: 'https://www.threads.net' },
  ] as const

  return (
    <footer className="bg-white text-slate-600">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:py-12">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-10">
          <div className="space-y-4">
            <div className="flex flex-col items-start gap-1">
              <AppLogo variant="light" showText iconHeight="h-9" />
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-slate-500">{t('home.footer.description')}</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              {t('home.footer.servicesHeading')}
            </h3>
            <ul className="mt-4 list-none space-y-3 p-0">
              {servicesLinks.map(({ key, to }) => (
                <li key={key}>
                  <Link to={to} className={footerLinkClass}>
                    {t(`home.footer.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              {t('home.footer.companyHeading')}
            </h3>
            <ul className="mt-4 list-none space-y-3 p-0">
              {companyLinks.map(({ key, to }) => (
                <li key={key}>
                  <Link to={to} className={footerLinkClass}>
                    {t(`home.footer.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              {t('home.footer.legalHeading')}
            </h3>
            <ul className="mt-4 list-none space-y-3 p-0">
              {legalLinks.map(({ key, to }) => (
                <li key={key}>
                  <Link to={to} className={footerLinkClass}>
                    {t(`home.footer.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <hr className="mt-10 border-t border-slate-200" />

        <div className="pt-6">
          <p className="text-center text-xs text-slate-400">{t('home.footer.copyrightTagline')}</p>
          <div className="mt-3 flex items-center justify-center gap-5 text-slate-400">
            {social.map(({ Icon, labelKey, href }) => (
              <a
                key={labelKey}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 transition-colors hover:text-imovPrimary focus:outline-none focus:ring-0"
                aria-label={t(labelKey)}
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
